import { useThemeContext } from './ThemeProvider';

/**
 * Hook for accessing and customizing theme
 *
 * @example
 * ```tsx
 * const { theme, setOverride, exportTheme } = useTheme();
 *
 * // Access theme values
 * <div style={{ color: theme.colors.brandPrimary }} />
 *
 * // Override theme (admin preview)
 * setOverride({
 *   colors: { brandPrimary: '#ff0000' }
 * });
 *
 * // Export theme to JSON
 * const json = exportTheme();
 * downloadFile('theme.json', json);
 *
 * // Import theme from JSON
 * const success = importTheme(uploadedJson);
 * ```
 */
export const useTheme = () => {
  const {
    theme,
    hasOverride,
    setOverride,
    resetTheme,
    exportTheme,
    importTheme,
    cssVariables,
  } = useThemeContext();

  return {
    /**
     * The current merged theme
     */
    theme,
    /**
     * Shorthand access to colors
     */
    colors: theme.colors,
    /**
     * Shorthand access to typography
     */
    typography: theme.typography,
    /**
     * Shorthand access to spacing
     */
    spacing: theme.spacing,
    /**
     * Shorthand access to border radius
     */
    borderRadius: theme.borderRadius,
    /**
     * Shorthand access to shadows
     */
    shadows: theme.shadows,
    /**
     * Shorthand access to borders
     */
    borders: theme.borders,
    /**
     * Whether a theme override is applied
     */
    hasOverride,
    /**
     * Update theme with new overrides (for admin preview)
     */
    setOverride,
    /**
     * Reset to base theme (clears overrides)
     */
    resetTheme,
    /**
     * Export current theme as JSON string
     */
    exportTheme,
    /**
     * Import theme from JSON string
     * Returns true if successful, false if invalid
     */
    importTheme,
    /**
     * CSS variables generated from theme
     */
    cssVariables,
  };
};

export default useTheme;
